import TreeItem from "@mui/lab/TreeItem";
import TreeView from "@mui/lab/TreeView";
import {
  ClickAwayListener,
  Grid,
  MenuItem,
  Popover,
  SvgIcon,
  Typography,
  useTheme,
  Theme,
  Box,
  alpha,
} from "@mui/material";
import { IReportTreeNode, ReportType } from "@vmosolutions/oc-interfaces";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FiletreeIconOpenPartial from "src/components/FileTree/filetree-icon-open-partial.svg?react";
import FiletreeIconOpen from "src/components/FileTree/filetree-icon-open.svg?react";
import FiletreeIcon from "src/components/FileTree/filetree-icon.svg?react";
import { IconButton } from "src/components/IconButton/IconButton";

import AssessmentIcon from "@mui/icons-material/Assessment";
import FolderIcon from "@mui/icons-material/Folder";
import FileIcon from "@mui/icons-material/InsertDriveFile";

import FolderOpenIcon from "@mui/icons-material/FolderOpen";

import classNames from "classnames";

// API Calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IFullReport } from "src/components/Reporting/ReportDetails";
import { updateReportNodes } from "src/redux/actions/Reporting/Reporting.Actions";

const treeViewStyles = (theme: Theme): any => ({
  "& .MuiTreeItem-root.file .MuiTreeItem-content": {
    "& .MuiTreeItem-iconContainer": { display: "none" },
  },
  "& .no_report": { margin: "10px" },
});

interface IReportTreeViewProps {
  rootReportNode: IReportTreeNode;
  defaultNode: any;
  setActiveNode: React.Dispatch<React.SetStateAction<IReportTreeNode>>;
  activeNode: IReportTreeNode;
}

/**
 * Recursively traverse report rootReportNode and append any folder node ids to an array.
 * @param treeNode - root node and then all nested nodes.
 * @param idArray - idArray which gets all ids pushed to it.
 */
export const getFolderIds = (treeNode: any, idArray: string[]): void => {
  treeNode?.children?.forEach((child: any) => {
    // console.log("child", child);
    if (child?.nodeType === "folder") {
      idArray.push(child._id);
      getFolderIds(child, idArray);
    }
  });
};

/**
 * Render Report Tree View
 * @param props - IReportTreeViewProps
 */
export const ReportTreeView = (props: IReportTreeViewProps): JSX.Element => {
  const { rootReportNode, defaultNode, setActiveNode, activeNode } = props;

  const dispatch = useDispatch();
  const reportTreeRef = useRef<HTMLDivElement>(null);
  const [contextAnchor, setContextAnchor] = useState<EventTarget>(null);
  const [message, setMessage] = useState<string>("Loading available reports...");

  // Create a folder node id array so that it can be used for the expand/collapse all functionality.
  const folderNodeIdArray = useMemo(() => {
    const folders: string[] = [];
    getFolderIds(rootReportNode, folders);
    return folders;
  }, [rootReportNode]);

  // Will now default to expanded mode showing all content.
  const [expanded, setExpanded] = useState<string[]>([]);

  // Ensure that if the folderNodeIdArray is updated, that the expanded is set to default this value
  useEffect(() => setExpanded(folderNodeIdArray), [folderNodeIdArray]);

  const expandToggleIcon = useMemo(() => {
    return folderNodeIdArray?.length === expanded?.length ? (
      <FiletreeIconOpen />
    ) : expanded?.length === 0 ? (
      <FiletreeIcon />
    ) : (
      <FiletreeIconOpenPartial />
    );
  }, [folderNodeIdArray, expanded]);

  useEffect(() => {
    const getReportsAction = async (): Promise<void> => {
      const reports = await AmplifyAPIWrapper.Get<IReportTreeNode[]>("/reports");
      if (Array.isArray(reports) && reports.length > 0) {
        dispatch(updateReportNodes(reports));
      } else {
        setMessage("No reports created yet, use the buttons above to create and save a report.");
      }
    };
    getReportsAction();
  }, [dispatch]);

  /** New Expand/Collapse All nodes feature */
  const handleToggleExpandAll = useCallback(() => {
    setExpanded((prev) => (prev.length === 0 ? folderNodeIdArray : []));
  }, [folderNodeIdArray]);

  const handleToggleExpandFolder = useCallback((event: any, nodeId: React.SetStateAction<string[]>) => {
    setExpanded(nodeId);
  }, []);

  const handleCopyNode = useCallback(
    async (node: IReportTreeNode): Promise<void> => {
      const itemType = node?.nodeType === ReportType.folder ? "Folder" : "Report";
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `Copying Selected ${itemType}...`,
        successMessage: `${itemType} Successfully Copied.`,
        errorMessage: `Error: Unable to Copy ${itemType}`,
      };
      const fullReport = await AmplifyAPIWrapper.Get<IFullReport>(`/reports/${node?._id}`);
      if (fullReport?._id) {
        const nodeCopy = { ...fullReport };
        nodeCopy.name = `${node.name} - copy`;
        const newNodes = await AmplifyAPIWrapper.Post<IReportTreeNode>("/reports", nodeCopy, notifications);
        if (Array.isArray(newNodes) && newNodes.length > 0) {
          dispatch(updateReportNodes(newNodes));
        }
      }
    },
    [dispatch]
  );

  const handleNodeSelect = useCallback(
    (node: IReportTreeNode) => {
      setActiveNode(node);
    },
    [setActiveNode]
  );

  // Get a modal on this when possible.
  const deleteNode = async (id: string): Promise<void> => {
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Deleting Selected Report...",
      successMessage: "Report Deleted",
      errorMessage: "Error: Unable To Delete Report",
    };
    const newNodes = await AmplifyAPIWrapper.Del<IReportTreeNode>(`/reports/${id}`, null, notifications);
    if (Array.isArray(newNodes) && newNodes.length > 0) {
      // console.log("deleted report", newNodes);
      dispatch(updateReportNodes(newNodes));
      setActiveNode(null);
    }
  };

  const handleContext = useCallback(
    (node: IReportTreeNode) => (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleNodeSelect(node);
      setContextAnchor(event.currentTarget);
    },
    [handleNodeSelect]
  );

  const closeContextMenu = (): void => {
    setContextAnchor(null);
  };

  const handleAnotherRightClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.preventDefault();
    closeContextMenu();
  };

  const getTreeFromPath = useCallback(
    (node: IReportTreeNode): JSX.Element => {
      const childrenNodes = node?.children?.sort((a: IReportTreeNode, b: IReportTreeNode) =>
        a.name < b.name ? -1 : 1
      );
      return (
        <>
          {childrenNodes.map((node: IReportTreeNode) => {
            if (node?.nodeType !== "folder") {
              return (
                <TreeItem
                  classes={{
                    root: classNames(activeNode?._id === node._id && "active", "file"),
                    label: "item_label",
                  }}
                  key={node._id}
                  nodeId={node._id}
                  onContextMenu={handleContext(node)}
                  onClick={(event) => {
                    event.stopPropagation(); // Required to ensure catch all for clearing selections doesn't trigger.
                    handleNodeSelect(node);
                  }}
                  label={
                    <>
                      {node?.nodeType === "file" ? (
                        <FileIcon color="inherit" className="item_icon" />
                      ) : (
                        <AssessmentIcon color="inherit" className="item_icon" />
                      )}
                      {node.name}
                    </>
                  }
                />
              );
            }
            return (
              <TreeItem
                classes={{
                  root: classNames(activeNode?._id === node._id && "active_folder", "folder"),
                  label: "item_label",
                }}
                key={node._id}
                nodeId={node._id}
                onContextMenu={handleContext(node)}
                onClick={(event) => {
                  event.stopPropagation(); // Required to ensure catch all for clearing selections doesn't trigger.
                  handleNodeSelect(node);
                }}
                label={node.name}
              >
                {getTreeFromPath(node)}
              </TreeItem>
            );
          })}
        </>
      );
    },
    [handleNodeSelect, handleContext, activeNode]
  );

  const reportTree = useMemo(() => {
    return rootReportNode.children.length === 0 ? (
      <Typography color="textPrimary" variant="body2" className="no_report">
        {message}
      </Typography>
    ) : (
      getTreeFromPath(rootReportNode)
    );
  }, [rootReportNode, message, getTreeFromPath]);

  const fileTreeViewStyles = (theme: Theme): any => ({
    root: {
      width: "100%",
      "& .context_listener": {
        "& ul.tree_view": {
          width: "100%",
          margin: 0,
          listStyle: "none",
          "& li.file": {
            "& .MuiTreeItem-iconContainer": {
              display: "none",
            },
            "& .item_label": {
              color: theme.custom.treeview.itemColor,
            },
          },
          "& li.folder": {
            "& .item_label.folder": {
              color: theme.custom.treeview.itemActiveColor,
            },
          },

          "& li.folder, & li.file": {
            "& .MuiTreeItem-iconContainer": {
              width: "18px",
              margin: "0px 4px 3px 6px",
              "& svg": {
                width: "16px",
                color: theme.custom.treeview.itemActiveColor,
              },
              "& .item_label": {
                userSelect: "none",
              },
            },
            "&.active_folder > .MuiTreeItem-content": {
              backgroundColor: alpha(theme.custom.treeview.itemActiveBackground, 0.1),
              "& .MuiTreeItem-iconContainer": {
                "& svg": {
                  fill: theme.custom.treeview.itemActiveColor,
                },
              },
              "& .item_label": {
                fill: theme.custom.treeview.itemActiveColor,
              },
            },
            "& .item_label": {
              display: "flex",
              alignItems: "center",
              overflowWrap: "break-word",
              whiteSpace: "normal",
              userSelect: "none",
              ...theme.typography.body2,
              color: theme.custom.treeview.itemColor,
              "& .item_icon": {
                width: "16px",
                margin: "0px 10px 0px 2px",
                color: theme.custom.treeview.itemColor,
              },
            },
            "&.active > .MuiTreeItem-content": {
              backgroundColor: theme.custom.treeview.itemActiveBackground,
              "& .item_label": {
                color: theme.custom.treeview.itemActiveColor,
                "& .item_icon": {
                  color: theme.custom.treeview.itemActiveColor,
                },
              },
            },
          },
          "& li.folder:focus > .MuiTreeItem-content": {
            backgroundColor: "transparent",
          },

          "& .MuiTreeItem-group": {
            marginLeft: 0,
            "& li.MuiTreeItem-root .MuiTreeItem-content": { paddingLeft: "10px" },
            "& .MuiTreeItem-group": {
              "& li.MuiTreeItem-root .MuiTreeItem-content": { paddingLeft: "20px" },
              "& .MuiTreeItem-group": {
                "& li.MuiTreeItem-root .MuiTreeItem-content": { paddingLeft: "30px" },
                "& .MuiTreeItem-group": {
                  "& li.MuiTreeItem-root .MuiTreeItem-content": {
                    paddingLeft: "40px",
                  },
                },
              },
            },
          },
        },
      },
    },

    templateContextMenu: {
      boxShadow: theme.shadows[1],
      borderRadius: "33px",
      backgroundColor: alpha(theme.palette.common.black, 0.85),
      fontSize: 10,
      lineHeight: "10px",
      "& .template_popover_menu": {
        padding: 0,
        "& li": {
          minHeight: "24px",
          color: theme.custom.timeline.groupColor,
          padding: "5px 20px",
          fontSize: 14,
          "&:hover": { color: theme.palette.text.primary },
        },
      },
    },
  });
  const fileTreeContainerStyles = (theme: Theme): any => ({
    root: {
      width: "245px",
      "& .filetree_header": {
        display: "flex",
        flexDirection: "column",
        margin: "10px 0px",
        "& .filetree_title": {
          display: "flex",
          alignItems: "center",
          "& .toggle_drawer_button": {
            width: "32px",
            height: "32px",
            margin: "5px",
          },
        },
        "& .filetree_buttons": {
          display: "flex",
          alignItems: "center",
          "& button": { margin: "5px" },
        },
      },
      "& .filetree_contents": {
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden",
        "& .no_templates": {
          margin: "0px 10px",
          fontSize: 14,
          fontWeight: 300,
          color: theme.custom.forms.inputText,
          maxWidth: "700px",
          whiteSpace: "normal",
        },
        "& .contextMenuAnchor": {
          width: "100%",
          height: "50px",
          top: "30px",
          position: "relative",
        },
      },
    },
  });
  return (
    <Grid
      container
      sx={(theme) => fileTreeContainerStyles(theme).root}
      direction="column"
      wrap="nowrap"
      style={{ height: "100%" }}
    >
      <Grid item className="filetree_header" style={{ width: "auto", flexShrink: 0 }}>
        <div className="filetree_title">
          <IconButton
            id="toggle_filetree_btn"
            className="toggle_drawer_button"
            size="small"
            onClick={handleToggleExpandAll}
          >
            <SvgIcon>{expandToggleIcon}</SvgIcon>
          </IconButton>
          <Typography className="toggle_drawer_text" variant="h6" color="textPrimary">
            Report Browser
          </Typography>
        </div>
      </Grid>

      <Grid
        item
        style={{ width: "100%", flex: 1, minHeight: 0, overflow: "auto" }}
        onClick={() => {
          setActiveNode(defaultNode);
        }}
      >
        <Box sx={(theme) => fileTreeViewStyles(theme).root}>
          <ClickAwayListener
            onClickAway={() => {
              setContextAnchor(null);
            }}
          >
            <div className="context_listener">
              <TreeView
                className="tree_view"
                defaultCollapseIcon={<FolderOpenIcon className="folder_open_icon" />}
                defaultExpandIcon={<FolderIcon className="folder_icon" />}
                defaultExpanded={expanded}
                expanded={expanded}
                onNodeToggle={handleToggleExpandFolder}
              >
                {reportTree}
              </TreeView>

              <div ref={reportTreeRef} className="contextMenuAnchor" />

              <div onContextMenu={handleAnotherRightClick} onClick={closeContextMenu}>
                <Popover
                  sx={(theme) => ({ paper: fileTreeViewStyles(theme).templateContextMenu })}
                  open={Boolean(contextAnchor)}
                  anchorEl={contextAnchor as Element}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  disableRestoreFocus
                >
                  {activeNode?.nodeType !== ReportType.folder && (
                    <MenuItem
                      key="copy_node_btn"
                      onClick={() => handleCopyNode(activeNode)}
                      disabled={!!activeNode?.readonly || activeNode?.nodeType !== ReportType.webdatarocks} // disable copy functionality if item is readonly.
                    >
                      Copy Report
                    </MenuItem>
                  )}
                  <MenuItem
                    key="delete_node_btn"
                    onClick={() => deleteNode(activeNode?._id)}
                    disabled={!!activeNode?.readonly || activeNode?.children?.length > 0} // disable delete functionality if item is readonly or has children.
                  >
                    Remove
                  </MenuItem>
                </Popover>
              </div>
            </div>
          </ClickAwayListener>
        </Box>
      </Grid>
    </Grid>
  );
};
